import React, { useEffect, useMemo } from 'react';
import { HbrDonutChart } from '@harbor/elements-react-standalone';
import { TileDefinition } from '../../../types/dashboard';
import type { TileSettings } from '../../../types/tileBuilderTypes';
import { buildHarborItemTooltip } from '../../../support/harborTooltip';
import { getContextualChartMargins } from './chartLayoutUtils';
import { makeSmartValueFormatter } from './tileUtils';

interface ChartData {
  id: string;
  label: string;
  value: number;
}

interface DonutChartTileProps {
  data: ChartData[];
  tileDef: TileDefinition;
  layoutHeight: number;
  layoutWidth: number;
  variant?: string;
  showLegendOverride?: boolean;
  setError?: (error: string) => void;
  initialized?: boolean;
  displayContext?: 'preview' | 'dashboard' | 'leader';
}

const DonutChartTile: React.FC<DonutChartTileProps> = ({
                                                         tileDef,
                                                         data,
                                                         layoutHeight,
                                                         layoutWidth,
                                                         variant = 'donut',
                                                         showLegendOverride,
                                                         setError,
                                                         initialized = false,
                                                         displayContext = 'dashboard',
                                                       }) => {
  const s = useMemo(() => (tileDef?.settings ?? {}) as TileSettings & any, [tileDef?.settings]);

  const isValidArray = Array.isArray(data);
  const dataArray = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const w = Math.max(1, layoutWidth || 0);
  const h = Math.max(1, layoutHeight || 0);

  const popoverOptions = useMemo(
    () => ({
      hoist: s?.tooltipHoist !== false,
      placement: s?.tooltipPlacement ?? 'top',
      distance: typeof s?.tooltipDistance === 'number' ? s.tooltipDistance : 8,
      skidding: typeof s?.tooltipSkidding === 'number' ? s.tooltipSkidding : 0,
    }),
    [s]
  );

  useEffect(() => {
    if (initialized && !isValidArray) {
      console.error('Expected donut data to be an array, but got:', data);
      setError?.('Invalid data format');
    }
  }, [initialized, isValidArray, data, setError]);

  const total = useMemo(() => {
    return dataArray.reduce((acc, cur) => acc + (Number(cur?.value) || 0), 0);
  }, [dataArray]);

  const valueFormatter = useMemo(
    () =>
      makeSmartValueFormatter({
        valueFormat: s?.valueFormat,
        currency: s?.currency,
        numberNotation: s?.numberNotation,
        compactThreshold: s?.compactThreshold,
        compactDecimals: s?.compactDecimals,
        prefixCurrencyOnCompact: s?.prefixCurrencyOnCompact,
      }),
    [s]
  );

  const showLegend = useMemo(() => {
    if (showLegendOverride !== undefined) return showLegendOverride;
    return layoutHeight > 150 && !!(tileDef?.show_legend || s?.show_legend);
  }, [showLegendOverride, layoutHeight, tileDef?.show_legend, s?.show_legend]);

  const layoutStrategy: 'auto' | 'manual' =
    (s as any)?.layoutStrategy === 'manual' ? 'manual' : 'auto';

  const computedMargin = useMemo(() => {
    const manualMargin = getContextualChartMargins(s, displayContext, showLegend, {
      top: 30,
      right: 10,
      bottom: 55,
      left: showLegend ? 150 : 10,
    });

    if (layoutStrategy === 'manual' && manualMargin) {
      return manualMargin;
    }

    const top = typeof s?.marginTop === 'number' ? s.marginTop : 30;
    const right = typeof s?.marginRight === 'number' ? s.marginRight : 10;
    const bottom = typeof s?.marginBottom === 'number' ? s.marginBottom : 55;
    const left = typeof s?.marginLeft === 'number' ? s.marginLeft : showLegend ? 150 : 10;

    return { top, right, bottom, left };
  }, [
    s,
    displayContext,
    showLegend,
    layoutStrategy,
  ]);

  const innerRadius = useMemo(() => {
    return typeof s?.innerRadius === 'number' ? s.innerRadius : 0.7;
  }, [s?.innerRadius]);

  const enableArcLinkLabels = useMemo(() => !!s?.enableArcLinkLabels, [s?.enableArcLinkLabels]);
  const enableArcLabels = useMemo(() => !!s?.enableArcLabels, [s?.enableArcLabels]);

  const itemTooltip = useMemo(() => {
    return buildHarborItemTooltip(
      s,
      {
        getSeries: (e: any) =>
          e?.id ?? e?.key ?? e?.data?.id ?? e?.data?.key ?? e?.data?.label ?? '',
        getLabel: (e: any) => e?.data?.label ?? e?.label ?? e?.data?.id ?? e?.id ?? '',
        getValue: (e: any) => {
          const raw = e?.data?.value ?? e?.value ?? '';
          const n = Number(raw);
          return Number.isFinite(n) ? valueFormatter(n) : raw;
        },
      },
      { dotMode: 'before' }
    );
  }, [s, valueFormatter]);

  const legends = useMemo(() => {
    if (!showLegend) return [];
    return [
      {
        direction: s?.legendDirection ?? 'column',
        align: 'left',
        translateX: typeof s?.legendTranslateX === 'number' ? s.legendTranslateX : -200,
        translateY: typeof s?.legendTranslateY === 'number' ? s.legendTranslateY : -10,
        itemWidth: typeof s?.legendItemWidth === 'number' ? s.legendItemWidth : 104,
        itemHeight: typeof s?.legendItemHeight === 'number' ? s.legendItemHeight : 20,
        itemsSpacing: typeof s?.legendItemsSpacing === 'number' ? s.legendItemsSpacing : 5,
        symbolSize: typeof s?.legendSymbolSize === 'number' ? s.legendSymbolSize : 18,
      },
    ];
  }, [
    showLegend,
    s?.legendDirection,
    s?.legendTranslateX,
    s?.legendTranslateY,
    s?.legendItemWidth,
    s?.legendItemHeight,
    s?.legendItemsSpacing,
    s?.legendSymbolSize,
  ]);

  const centerContent = useMemo(() => {
    const centerMode = (s.centerMode ?? 'total') as 'none' | 'total' | 'custom' | 'slot';

    if (centerMode === 'none') return undefined;

    if (centerMode === 'custom') {
      const html = String(s.centerHtml ?? '').trim();
      if (!html) return undefined;
      return () => html;
    }

    if (centerMode === 'slot') return undefined;

    return () => `
      <div style="display:flex;height:100%;flex-direction:column;justify-content:center;align-items:center;">
        <div style="font-size:var(--hbr-font-size-2xl);font-weight:var(--hbr-font-weight-bold,700)">${valueFormatter(total)}</div>
      </div>
    `;
  }, [s.centerMode, s.centerHtml, total, valueFormatter]);

  const config = useMemo(() => {
    const base: any = {
      data: dataArray,
      margin: computedMargin,
      innerRadius,
      legends,
      enableArcLinkLabels,
      enableArcLabels,
      itemTooltip,
    };

    if (centerContent) base.centerContent = centerContent;

    base.popoverOptions = {
      ...(base.popoverOptions || {}),
      hoist: s?.tooltipHoist !== false,
      placement: s?.tooltipPlacement ?? 'top',
      distance: typeof s?.tooltipDistance === 'number' ? s.tooltipDistance : 8,
      skidding: typeof s?.tooltipSkidding === 'number' ? s.tooltipSkidding : 0,
    };

    return base;
  }, [
    dataArray,
    computedMargin,
    innerRadius,
    legends,
    enableArcLinkLabels,
    enableArcLabels,
    itemTooltip,
    centerContent,
    s?.tooltipHoist,
    s?.tooltipPlacement,
    s?.tooltipDistance,
    s?.tooltipSkidding,
  ]);

  const modifyHbrConfig = useMemo(() => {
    return (cfg: any) => {
      const next = { ...(cfg || {}) };
      next.popoverOptions = {
        ...(next.popoverOptions || {}),
        hoist: s?.tooltipHoist !== false,
        placement: s?.tooltipPlacement ?? 'top',
        distance: typeof s?.tooltipDistance === 'number' ? s.tooltipDistance : 8,
        skidding: typeof s?.tooltipSkidding === 'number' ? s.tooltipSkidding : 0,
      };
      next.tooltipZIndex = next.tooltipZIndex ?? 9999;
      return next;
    };
  }, [s]);

  if (!setError && initialized && !isValidArray) {
    return <div>Invalid data format</div>;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: showLegend ? 'flex-start' : 'center',
        alignItems: 'center',
        // DEFINITE pixel box (the measured layoutWidth×layoutHeight) on the WRAPPER, with the chart
        // filling it at 100%. HbrDonutChart (nivo) measures its parent with an internal ResizeObserver;
        // with a percentage-size chain and no definite-size ancestor, the loaded donut's drawn SVG
        // nudges the auto-size parent, nivo re-measures, re-draws, and the two ResizeObservers fight —
        // the "goes haywire after load" blink. Pinning the PARENT to a fixed `w`×`h` box (measured
        // integers, floored non-zero on mount, only changing on a real ≥2px resize now) gives nivo a
        // stable box to measure so the feedback can't run. The chart itself MUST stay width/height
        // "100%": HbrDonutChart renders blank if handed numeric width/height. It re-fits on genuine
        // resizes via the ${w}x${h} remount key.
        width: w,
        height: h,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <HbrDonutChart
        key={`${variant}-${w}x${h}-${displayContext}-${showLegend ? 'legend' : 'base'}`}
        variant={variant as 'donut' | 'pie' | 'gauge'}
        width="100%"
        height="100%"
        config={config}
        modifyHbrConfig={modifyHbrConfig}
        popoverOptions={popoverOptions as any}
      />
    </div>
  );
};

export default DonutChartTile;